import { format } from 'date-fns';
import clsx from 'clsx';
import { intensityClass, DAY_OFF_MARKER } from '@/components/queue/availabilityCell';
import type { ArtistMonth, ArtistMonthDay } from '@/domain/artistMonth';

interface Props {
  month: ArtistMonth;
  /** The day whose breakdown is showing, if any. */
  selectedDayIso: string | null;
  onSelect: (dayIso: string) => void;
}

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const key = (dayIso: string) => format(new Date(dayIso), 'yyyy-MM-dd');

function ariaLabel(day: ArtistMonthDay): string {
  const when = format(new Date(day.dayIso), 'EEE d MMM');
  if (!day.inMonth) return `${when}, outside this month`;
  if (day.kind === 'rdo') return `${when}, rostered off`;
  if (day.kind === 'comp' || day.kind === 'leave') {
    return `${when}, ${DAY_OFF_MARKER[day.kind].label}${day.note ? ` — ${day.note}` : ''}`;
  }
  return `${when}, ${day.openHours}h open of ${day.rosteredHours}h rostered`;
}

/**
 * The shape of one day: dark runs are time in the chair, void runs are open.
 * Two days can both read "2h open" and look nothing alike — two one-hour slivers
 * against a single two-hour block — and this strip is the only thing on the grid
 * that says so without a click.
 */
function DayShape({ day }: { day: ArtistMonthDay }) {
  if (day.segments.length === 0 || day.rosteredHours <= 0) return null;
  return (
    <div className="mt-1.5 flex h-[5px] overflow-hidden rounded-full bg-ink-black/60">
      {day.segments.map((seg, i) => (
        <span
          key={`${seg.kind}-${i}`}
          style={{ width: `${(seg.hours / day.rosteredHours) * 100}%` }}
          className={seg.kind === 'booked' ? 'bg-cream/60' : 'bg-ink-black/30'}
        />
      ))}
    </div>
  );
}

/**
 * One artist's month, on the same traffic-light scale the agents and the sweeper
 * read: a full book is healthy, a wide-open day is lost income. The colour and the
 * off-day markers come from the shared availability-cell helpers, so this grid and
 * the agent grids cannot drift apart.
 *
 * Presentational: the selected day and the breakdown below it belong to the caller.
 */
export default function ArtistMonthGrid({ month, selectedDayIso, onSelect }: Props) {
  const selectedKey = selectedDayIso ? key(selectedDayIso) : null;

  return (
    <div data-testid="artist-month-grid">
      <div className="overflow-x-auto">
        <div className="grid min-w-[640px] grid-cols-7 gap-1.5">
          {DOW_LABELS.map((label) => (
            <p
              key={label}
              data-testid={`month-dow-${label.toLowerCase()}`}
              className="pb-1 text-center font-inter text-[9px] font-semibold uppercase tracking-widest text-cream-faint"
            >
              {label}
            </p>
          ))}

          {month.days.map((day) => {
            const dayKey = key(day.dayIso);
            const marker =
              day.kind === 'comp' || day.kind === 'leave' ? DAY_OFF_MARKER[day.kind] : null;
            const off = day.kind !== 'working';
            const selectable = day.inMonth && !off;
            const isSelected = selectedKey === dayKey;

            return (
              <button
                key={day.dayIso}
                type="button"
                data-testid={`month-cell-${dayKey}`}
                aria-label={ariaLabel(day)}
                {...(isSelected ? { 'aria-current': 'true' } : {})}
                disabled={!selectable}
                onClick={() => selectable && onSelect(day.dayIso)}
                title={ariaLabel(day)}
                className={clsx(
                  'relative flex h-[82px] flex-col justify-between rounded-md border p-1.5 text-left transition',
                  !day.inMonth
                    ? 'border-transparent bg-ink-deep/60 opacity-55'
                    : marker
                      ? clsx('border-transparent', marker.cls)
                      : off
                        ? 'border-transparent bg-ink-soft/40'
                        : clsx(
                            'border-transparent',
                            intensityClass(day.openHours, day.rosteredHours),
                          ),
                  day.past && day.inMonth && 'opacity-45',
                  day.isToday && '!border-gold-bright border-2 p-[5px]',
                  isSelected && '!border-gold-bright border-2 p-[5px] shadow-gold-glow',
                  selectable ? 'cursor-pointer hover:border-gold-bright/70' : 'cursor-default',
                )}
              >
                {day.isToday ? (
                  <span className="absolute right-1.5 top-1 font-inter text-[8px] font-bold uppercase tracking-widest text-gold">
                    Today
                  </span>
                ) : marker ? (
                  <span className="absolute right-1.5 top-1 font-mono text-[11px] font-bold text-cream/75">
                    {marker.letter}
                  </span>
                ) : null}

                <span
                  className={clsx(
                    'font-mono text-xs tabular-nums',
                    day.inMonth ? 'text-cream-soft' : 'text-cream-faint',
                  )}
                >
                  {day.dayOfMonth}
                </span>

                {!day.inMonth ? null : marker ? (
                  <span className="font-inter text-[9px] font-semibold uppercase tracking-wider text-cream/70">
                    {marker.label}
                  </span>
                ) : off ? (
                  <span className="font-mono text-lg leading-none text-cream-faint">·</span>
                ) : (
                  <span>
                    <span className="font-mono text-[19px] leading-none tabular-nums">
                      {day.openHours}
                      <em className="ml-0.5 text-[10px] not-italic tracking-wide">h open</em>
                    </span>
                    <DayShape day={day} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 font-inter text-[10px] uppercase tracking-widest text-cream-soft">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-5 rounded-sm bg-mint/55" /> Booked solid
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-5 rounded-sm bg-gold/55" /> 1–4h open
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-5 rounded-sm bg-blood/55" /> 5h+ open
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-5 rounded-sm bg-ink-soft/40" /> Rostered off
        </span>
        <span className="flex items-center gap-1.5">
          <span className="flex h-3 w-5 items-center justify-center rounded-sm bg-azure/45 font-mono text-[8px] text-ink-black">
            C
          </span>{' '}
          Comp day
        </span>
        <span className="flex items-center gap-1.5">
          <span className="flex h-3 w-5 items-center justify-center rounded-sm bg-gold-dim/55 font-mono text-[8px] text-ink-black">
            L
          </span>{' '}
          Leave
        </span>
        <span className="text-cream-faint">Faded days have already happened</span>
      </div>
    </div>
  );
}
